import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

/**
 * App-level confirmation toast for board actions (track a bet, slip
 * notices). Same architecture as {@link PostToFeedToast}: board rows
 * remount on every odds poll and their scroll containers use
 * `contain: paint` (which hijacks position:fixed descendants), so any
 * confirmation UI owned by a row is both short-lived and mispositioned.
 * This host mounts once at the app root, is triggered imperatively via
 * {@link promptBoardToast}, and auto-dismisses — confirming an action
 * never blocks the board or demands an explicit Close.
 */

const PROMPT_EVENT = "bb:board-toast";
const TOAST_MS = 5000;

export interface BoardToastDetail {
  message: string;
  /** Optional trailing action, e.g. "My Bets →" navigating to /my-bets. */
  actionLabel?: string | null;
  actionTo?: string | null;
}

/** Show an auto-dismissing confirmation toast. */
export const promptBoardToast = (detail: BoardToastDetail): void => {
  window.dispatchEvent(new CustomEvent(PROMPT_EVENT, { detail }));
};

const BoardToast: React.FC = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState<BoardToastDetail | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      const detail = (e as CustomEvent).detail as BoardToastDetail | undefined;
      if (!detail?.message) return;
      setToast(detail);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setToast(null), TOAST_MS);
    };
    window.addEventListener(PROMPT_EVENT, onPrompt);
    return () => {
      window.removeEventListener(PROMPT_EVENT, onPrompt);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!toast) return null;

  const hasAction = Boolean(toast.actionLabel && toast.actionTo);

  return createPortal(
    <div
      role="status"
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        /* Clear of the mobile bottom bar (zero-height on desktop) and in easy
         * thumb reach on phones — same anchor as the feed toast. */
        bottom:
          "calc(var(--bb-bottomnav-h, 0px) + env(safe-area-inset-bottom, 0px) + 26px)",
        zIndex: 10050,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        backgroundColor: "#0A1E3B",
        border: "1px solid rgba(52, 211, 153, 0.45)",
        borderRadius: "999px",
        padding: "10px 18px",
        fontSize: "13px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        maxWidth: "calc(100vw - 24px)",
        boxShadow: "0 14px 36px -10px rgba(2, 8, 20, 0.9)",
        animation: "bb-board-toast-in 180ms ease-out",
      }}
    >
      <style>{`@keyframes bb-board-toast-in { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
      <svg
        viewBox="0 0 16 16"
        width="14"
        height="14"
        fill="none"
        stroke="#34d399"
        strokeWidth="2.5"
        style={{ flexShrink: 0 }}
      >
        <path d="M3 8.5 6.5 12 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <button
        type="button"
        onClick={() => setToast(null)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          color: "#e6f3fb",
          font: "inherit",
          cursor: "pointer",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {toast.message}
      </button>
      {hasAction && (
        <>
          <span style={{ color: "rgba(148, 194, 220, 0.4)" }}>·</span>
          <button
            type="button"
            onClick={() => {
              setToast(null);
              navigate(toast.actionTo as string);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#00ffff",
              font: "inherit",
              cursor: "pointer",
            }}
          >
            {toast.actionLabel}
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

export default BoardToast;
